import { inject, Injectable, signal } from '@angular/core';
import { FeatureTogglesApi, type OmitIdTimestamp } from '@reconcept-platform/shared/data-access';
import { type FeatureToggle } from '@reconcept-platform/shared/types';
import { transform } from 'lodash-es';
import { catchError, of, take, tap } from 'rxjs';
import { map } from 'rxjs/operators';

// based on https://medium.com/@iamjustin/feature-flags-in-angular-d50a2b8437fe
type FeatureToggleResponse = {
  useRecordAudioForSpeechToText: boolean;
  milestonesAndRotations: boolean;
  experimentalDarkMode: boolean;
  showCccReporting: boolean;
  showCccReportingApp: boolean;
  skillDetailTrendInfo: boolean;
  newRefresherCourseWeb: boolean;
  showAppRefresherCourseTile: boolean;
  appCpdCalendarHomeTile: boolean;
  appGaiaProfileMenuItem: boolean;
  showStreak: boolean;
  showBadges: boolean;
  showSpfNotes: boolean;
  showSpfFeedback: boolean;
  enableVirusScanUploadProxy: boolean;
  enableActionCableConnections: boolean;

  // the key for the restyles are dasherized, so we can use them directly as classes
  '-restyle_ft--border-radius': boolean;
  '-restyle_ft--DUMMY': boolean;
  '-restyle_ft--no-margin': boolean;
};

type _FeatureToggleKeys = keyof FeatureToggleResponse;

export type FeatureToggleKeys = {
  [K in _FeatureToggleKeys]: K;
}[_FeatureToggleKeys];

@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private featureTogglesApi = inject(FeatureTogglesApi);

  features = signal<Record<string, boolean>>({});

  // ALL flags should be false by default
  private defaultFeatureValues: FeatureToggleResponse = {
    useRecordAudioForSpeechToText: false,
    milestonesAndRotations: false,
    experimentalDarkMode: false,
    showCccReporting: false,
    showCccReportingApp: false,
    skillDetailTrendInfo: false,
    newRefresherCourseWeb: false,
    showAppRefresherCourseTile: false,
    appCpdCalendarHomeTile: false,
    appGaiaProfileMenuItem: false,
    showStreak: false,
    showBadges: false,
    showSpfNotes: false,
    showSpfFeedback: false,
    enableVirusScanUploadProxy: false,
    enableActionCableConnections: false,
    '-restyle_ft--border-radius': false,
    '-restyle_ft--DUMMY': false,
    '-restyle_ft--no-margin': false,
  };

  getFeatureToggles(activeOnly = true) {
    return this.getSavedFeatureToggles(activeOnly).pipe(
      map((featureToggles) => {
        const featureTogglesToMerge: Record<string, boolean> = {};
        featureToggles.forEach((featureToggle) => {
          featureTogglesToMerge[featureToggle.key] = featureToggle.active;
        });
        return this.mergeFeatureToggles(featureTogglesToMerge);
      }),
      tap((featureToggles) => this.features.set(featureToggles)),
      take(1),
    );
  }

  listFeatureToggles(activeOnly: boolean) {
    return this.getSavedFeatureToggles(activeOnly).pipe(
      map((featureToggles) => {
        return transform(Object.keys(this.defaultFeatureValues), this.transformFeatureToggle, [...featureToggles]);
      }),
    );
  }

  getFeature(feature: FeatureToggleKeys): boolean {
    // isDevMode is true when project.json: "optimization": false.
    // this is for dev & staging environments
    return this.features()[feature] ?? false;
  }

  mergeFeatureToggles(partialFeatureToggles: Partial<FeatureToggleResponse>): FeatureToggleResponse {
    // Because we don't know if all flags are already in the backend, and the front-end is leading in what keys exist,
    // we want to make sure that only the flags that are properly configured AND set as active, are actually set as active.
    return { ...this.defaultFeatureValues, ...partialFeatureToggles };
  }

  updateFeatureToggle(feature: OmitIdTimestamp<FeatureToggle>) {
    return this.featureTogglesApi.updateFeatureToggle({
      key: feature.key,
      activateOn: feature.activateOn,
    });
  }

  private getSavedFeatureToggles(activeOnly: boolean) {
    return this.featureTogglesApi
      .watchFeatureToggles(activeOnly)
      .pipe(catchError(() => of(transform(Object.keys(this.defaultFeatureValues), this.transformFeatureToggle))));
  }

  private transformFeatureToggle(
    featureToggles: Pick<FeatureToggle, 'key' | 'active' | 'activateOn'>[],
    featureToggle: string,
  ) {
    if (!featureToggles.some((feature) => feature.key === featureToggle))
      featureToggles.push({
        key: featureToggle,
        active: false,
        activateOn: null,
      });
  }
}
